import { RefObject, useContext, useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Container,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { ArrowBackIos, Edit } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useTicketApi } from "../../custom-hooks/apis/use-ticket-api";
import { useMutation, useQuery } from "react-query";
import { QueryKeys } from "../../global-state/react-query-keys";
import { toastError } from "../../utils/toaster";
import { IssuedNewTicket } from "../../types";
import ButtonSubmit from "../../components/Buttons/ButtonSubmit";
import ButtonRegular from "../../components/Buttons/ButtonRegular";
import AuthContext from "../../custom-hooks/use-auth-context";
import ErrorHandler from "../../components/ErrorHandler";
import Loader from "../../components/Loader";
import GlobalDataContext from "../../custom-hooks/use-global-data";
import ReloadAnimation from "../../components/ReloadAnimation";
import IssuedTicketDetailContent from "../../components/IssuedTicketDetailContent";
import { usePrintTicket } from "../../hooks/usePrintTicket"; 
import { loadSvgImage } from "../../utils/loadSvgImage";
const IssuedTicketDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<boolean>(false);
  const [qr, setQr] = useState<any | null>(null);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const { getIssuedTicket, getNewIssuedTicket } = useTicketApi();
  const authCtx = useContext(AuthContext);
  const { loadingOp } = useContext(GlobalDataContext);
  const refContainer: RefObject<HTMLDivElement> = useRef(null);
  const { printTicket } = usePrintTicket(); 

  const { isLoading, data, isError, isRefetching, isRefetchError } = useQuery(
    [
      QueryKeys.issuedTicket,
      authCtx.user?.signInUserSession.idToken,
      id,
      authCtx.user?.store_id,
    ],
    () => {
      if (
        authCtx.user?.signInUserSession.idToken &&
        id &&
        authCtx.user?.store_id
      ) {
        return getIssuedTicket({
          customerStoreId: authCtx.user.store_id,
          ticketId: id,
        });
      }
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const getNewIssuedTicketMutation = useMutation(
    (requestBody: IssuedNewTicket) => getNewIssuedTicket(requestBody),
    {
      onSuccess: async (data) => {
        setQr(data);
        setVisibility(true);
      },
      onError: async (error: any) => {
        console.error("新規発行エラー:", error);
        let msg = "申し訳ありませんが、新しく発行することができませんでした。";
        if (error.response?.data?.code) {
          msg = error.response?.data?.message;
        }
        loadingOp.finish();
        toastError(msg);
      },
    }
  );

  const handleIssuedNewTicket = () => {
    if (!authCtx.user?.store_id || !id) return;
    getNewIssuedTicketMutation.mutate({
      ticketId: id,
      customerStoreId: authCtx.user.store_id,
    });
  };

  const handlePrint = async () => {
    await printTicket(componentRef.current, () => {
      if (refContainer.current) {
        refContainer.current.innerHTML = "";
      }
      setVisibility(false);
    });
  };

  useEffect(() => {
    if (qr?.encodeImage && refContainer.current) {
      loadSvgImage(refContainer.current, qr.encodeImage);
    }
  }, [qr?.encodeImage]);

  if (isError || isRefetchError) return <ErrorHandler url="/store" />;

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: `calc(100vh - 76px)`, bgcolor: "#E6E4EB", padding: 0 }}
    >
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{ bgcolor: "#E6E4EB", width: 1, paddingY: "15px" }}
      >
        <Toolbar
          sx={{ width: 1, display: "flex", justifyContent: "space-between" }}
        >
          <Grid sx={{ minWidth: "64px" }}>
            <IconButton onClick={() => navigate("/store/issued-tickets")}>
              <ArrowBackIos />
            </IconButton>
          </Grid>
          <Typography
            variant="h6"
            sx={{
              color: "#4F4F62",
              fontWeight: 600,
              fontSize: "1.2rem",
              wordWrap: "break-word",
              wordBreak: "break-all",
            }}
          >
            チケット印刷
          </Typography>
          <ButtonSubmit
            type="button"
            disabled={isError}
            sx={{ py: "5px", px: "10px", minWidth: "40px", boxShadow: 5 }}
            onClick={() => navigate(`/store/issued-tickets/${id}/update`)}
          >
            <Grid sx={{ flexDirection: "column" }}>
              <Edit />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#fff",
                  mt: "-8px",
                  fontWeight: "bold",
                }}
              >
                編集
              </Typography>
            </Grid>
          </ButtonSubmit>
        </Toolbar>
      </AppBar>

      {isError ? (
        <ErrorHandler />
      ) : (
        <ReloadAnimation>
          <Box sx={{ minHeight: `calc(100vh - 160px)`, px: 2, pb: 2, mb: 7 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ButtonRegular
                  type="button"
                  sx={{
                    width: 1,
                    boxShadow: 2,
                    py: 1.5,
                    borderRadius: "30px",
                    transition: "background-color 0.3s",
                  }}
                  onClick={handleIssuedNewTicket}
                 
                >
                  新しく発行する
                </ButtonRegular>
              </Grid>
              <Grid item xs={6}>
                <ButtonSubmit
                  type="button"
                  sx={{ width: 1, boxShadow: 2, py: 1.5, borderRadius: "30px" }}
                  onClick={handlePrint}
                  disabled={!visibility || isPrinting}
                >
                  印刷する
                </ButtonSubmit>
              </Grid>
            </Grid>

            <IssuedTicketDetailContent
              qr={qr}
              data={data}
              isPrinting={isPrinting}
              visibility={visibility}
              refContainer={refContainer}
              componentRef={componentRef}
            />

            {(getNewIssuedTicketMutation.isLoading || isPrinting) && <Loader />}
          </Box>
        </ReloadAnimation>
      )}

      {(isLoading || isRefetching) && <Loader />}
    </Container>
  );
};

export default IssuedTicketDetail;
